/**
 *
 * The "links" duck. Used w/ TrackedLinks and link tracking.
 *
 * Generally speaking, keeps the data of the last link clicked in the store.
 *
 * This is, of course, slightly different from the browser history.
 *
 * Action Types:
 * - CAROUSEL_CHANGED
 * - ELEMENT_CLICKED
 * - ONBOARDING_CHANGED
 * - LINK_CLICKED
 * - CLEAR_LAST_LINK_CLICKED
 *
 * Action Creators:
 * - carouselChanged()
 * - elementClicked()
 * - onboardingChanged()
 * - linkClicked()
 * - clearLastLinkClicked()
 *
 */

import type {
  CarouselChanged,
  CarouselOpt,
  ElementClickedAction,
  LinkState,
  LinkClickedAction,
  OnboardingChanged,
  OnboardingOpt,
  PersistReducerConfig,
  clearLastLinkClickedAction,
  TrackingAction,
} from '../types';

/** @type {ActionType} */
export const CAROUSEL_CHANGED = 'PB/carousel/CAROUSEL_CHANGED';

/** @type {ActionType} */
export const ONBOARDING_CHANGED = 'PB/onboarding/ONBOARDING_CHANGED';

/** @type {ActionType} **/
/** The link clicked action: note, this reducer does not actually change the state directly,
 * but rather notes the link clicked. **/

export const LINK_CLICKED = 'PB/links/LINK_CLICKED';

/** @type {ActionType} **/
/** The element clicked clicked action: note, this reducer does not actually change the state directly,
 * but rather notes the element clicked. **/

export const ELEMENT_CLICKED = 'PB/links/ELEMENT_CLICKED';

/** @type {ActionType} **/
/** This action clears the linkClick in the state. */

export const CLEAR_LAST_LINK_CLICKED = 'PB/links/CLEAR_LAST_LINK_CLICKED';

/** @ignore
 * We start with the lastLink having a mostly blank slate, except it has the linkId 'pageload'.
 */

const initialState = {
  lastClick: {
    id: '',
  },
  lastLink: {
    id: 'pageload',
    href: '',
  },
};

// This is what will be persisted in the store.
export const persist: PersistReducerConfig = {
  whitelist: ['lastLink'],
  reducer: 'tracking',
};

/** @ignore **/
export default function tracking(
  state: LinkState = initialState,
  action: TrackingAction,
): LinkState {
  switch (action.type) {
    case LINK_CLICKED:
      const { link } = action.payload;
      const { lastClick } = state;
      return { lastLink: link, lastClick };

    case ELEMENT_CLICKED:
      const { lastLink } = state;
      return { lastClick: action.payload, lastLink };

    case CLEAR_LAST_LINK_CLICKED:
      return { ...state, lastLink: {} };

    default:
      return state;
  }
}
/**
 * This action creator will return a LINK_CLICKED action with
 * the link that was clicked, along with the id
 * and where the link was clicked (which url was present before the user clicked)
 *
 * @type {ActionCreator}
 *
 * @param {string} href - The href of the link that was clicked.
 * @param {string} id - The id of the link that was clicked, this is user defined, not to be confused with anchor/CSS id.
 * @param {string} additionalTrackingProps - Additional key-value pairs to track
 *
 */

export function linkClicked(
  href: string,
  id: string,
  additionalTrackingProps: Object,
): LinkClickedAction {
  return {
    type: LINK_CLICKED,
    payload: {
      link: {
        href,
        id,
        additionalTrackingProps,
      },
    },
  };
}

/**
 * This action creator will return an ELEMENT_CLICKED action with
 * the id of the element clicked
 *
 * @type {ActionCreator}
 *
 * @param {string} id - The id of the link that was clicked, this is user defined, not to be confused with anchor/CSS id.
 * @param {string} additionalTrackingProps - Additional key-value pairs to track
 *
 */

export function elementClicked(
  id: string,
  additionalTrackingProps: Object,
): ElementClickedAction {
  return {
    type: ELEMENT_CLICKED,
    payload: {
      id,
      additionalTrackingProps,
    },
  };
}

/**
 * This action creator will return a CAROUSEL_CHANGED action.
 *
 * @return {Action}
 * @property {string} type CAROUSEL_CHANGED
 * @propery {Object} payload
 */
export function carouselChanged({
  direction,
  index,
}: CarouselOpt): CarouselChanged {
  return {
    type: CAROUSEL_CHANGED,
    payload: { direction, index },
  };
}

/**
 * This action creator will return a ONBOARDING_CHANGED action.
 *
 * @return {Action}
 * @property {string} type ONBOARDING_CHANGED
 * @propery {Object} payload
 */
export function onboardingChanged({ index }: OnboardingOpt): OnboardingChanged {
  return {
    type: ONBOARDING_CHANGED,
    payload: { index },
  };
}

/**
 * This action creator will return a CLEAR_LAST_LINK_CLICKED action.
 *
 * @return {Action}
 * @property {string} type CLEAR_LAST_LINK_CLICKED
 */
export function clearLastLinkClicked(): clearLastLinkClickedAction {
  return {
    type: CLEAR_LAST_LINK_CLICKED,
    payload: {},
  };
}
